import { breadcrumbListNode, jsonLdGraph, productNode, type BreadcrumbInput, type JsonLdNode } from '~/utils/schemaOrg';
import { stripHtml, trimTrailingSlash, truncate } from '~/utils/seo';

export interface ProductSchemaOptions {
    product: MaybeRefOrGetter<any>;
    variants?: MaybeRefOrGetter<any[] | undefined>;
    breadcrumbs: MaybeRefOrGetter<BreadcrumbInput[]>;
}

export default function useProductSchema(options: ProductSchemaOptions) {
    const config = useRuntimeConfig();
    const baseUrl = trimTrailingSlash(String(config.public.prodUrl ?? ''));
    if (!baseUrl) return;

    const canonicalUrl = useCanonicalUrl();
    const siteUrl = `${baseUrl}/nl/`;
    const { sessionContext } = useSessionContext();

    const graph = computed(() => {
        const product = toValue(options.product);
        if (!product) return '';

        const manufacturerName = product.manufacturer?.translated?.name ?? product.manufacturer?.name;
        const productName = product.translated?.name ?? product.name ?? '';
        const colorProperty = (product.properties ?? []).find(
            (property: any) => property.groupId === config.public.shopwareColorPropertyId,
        );

        const images = [
            product.cover?.media?.url,
            ...(product.media ?? []).map((entry: any) => entry?.media?.url),
        ].filter((url, index, all) => !!url && all.indexOf(url) === index);

        const breadcrumbs = toValue(options.breadcrumbs) ?? [];
        // Same check the add-to-cart button uses. Without a configurator (no size options) there
        // is nothing to select, so fall back to the product's own availability flag.
        const variants = toValue(options.variants);
        const inStock = variants?.length ? !useProductState(variants).isSoldOut.value : product.available !== false;

        const nodes: Array<JsonLdNode | undefined> = [
            productNode({
                siteUrl,
                canonicalUrl,
                name: [manufacturerName, productName].filter(Boolean).join(' '),
                description: truncate(
                    stripHtml(product.translated?.description ?? product.description) ||
                        (product.translated?.metaDescription ?? product.metaDescription ?? ''),
                    5000,
                ),
                sku: product.productNumber,
                gtin13: product.ean,
                brandName: manufacturerName,
                color: colorProperty?.translated?.name ?? colorProperty?.name,
                category: breadcrumbs[breadcrumbs.length - 1]?.name,
                images,
                price: product.calculatedPrice?.totalPrice,
                priceCurrency: sessionContext.value?.currency?.isoCode ?? 'EUR',
                inStock,
            }),
            breadcrumbListNode({ baseUrl, canonicalUrl, items: breadcrumbs }),
        ];

        return jsonLdGraph(nodes);
    });

    useHead({
        script: [
            {
                key: 'schema-org-product',
                type: 'application/ld+json',
                innerHTML: graph,
            },
        ],
    });
}
